import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Tag, Sliders, Bell, Shield, LayoutGrid,
  Smartphone, Laptop, Lock, Mail, Moon, Sun, Check,
  AlertCircle, RefreshCw, LogOut, ChevronDown, Plus,
  Pencil, Trash2, ShieldAlert, Sparkles, CheckCircle2,
  Clock, Globe, Monitor
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { DASHBOARD_GROUPS, DASHBOARD_CARDS, PRESETS, resolveDashboardCards } from '../utils/dashboardCards';
import SettingsNav from '../components/SettingsNav';
import { formatDate } from '../utils/format';

// ── CPF/CNPJ mask ────────────────────────────────────────────────────────────
function maskCpfCnpj(raw) {
  const digits = raw.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

const TIPOS = ['RECEITA', 'DESPESA', 'CUSTO'];
const TIPO_LABEL = { RECEITA: 'Receita', DESPESA: 'Despesa', CUSTO: 'Custo' };
const TIPO_COLOR = {
  RECEITA: 'bg-emerald-100 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400',
  DESPESA: 'bg-rose-100 dark:bg-rose-950 text-rose-700 dark:text-rose-400',
  CUSTO: 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400',
};

function Toggle({ checked, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        checked ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-700'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? 'translate-x-[22px]' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

export default function Configuracoes() {
  const { tenant, refreshTenant } = useTenant();
  const { theme, toggleTheme } = useTheme();
  const { user, refreshUser } = useAuth();
  const accent = tenant?.corPrimaria || '#6366F1';

  const [tab, setTab] = useState('empresa');

  // ── Tab Empresa ──
  const [nomeResponsavel, setNomeResponsavel] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [empresaLoading, setEmpresaLoading] = useState(false);
  const [empresaSuccess, setEmpresaSuccess] = useState('');
  const [empresaError, setEmpresaError] = useState('');

  // ── Tab Categorias ──
  const [categorias, setCategorias] = useState([]);
  const [catLoading, setCatLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [deletingCat, setDeletingCat] = useState(null);
  const [filterTipo, setFilterTipo] = useState('');

  // ── Tab Segurança ──
  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [senhaConfirma, setSenhaConfirma] = useState('');
  const [senhaLoading, setSenhaLoading] = useState(false);

  // ── Tab Dispositivos Conectados ──
  const [sessoes, setSessoes] = useState([]);
  const [sessoesLoading, setSessoesLoading] = useState(false);

  // Carrega configurações da empresa
  useEffect(() => {
    api.get('/tenant/configuracoes')
      .then(res => {
        setNomeResponsavel(res.data.nomeResponsavel || '');
        setCpfCnpj(res.data.cpfCnpj ? maskCpfCnpj(res.data.cpfCnpj) : '');
      })
      .catch(() => {});
  }, []);

  const handleCpfCnpjChange = (e) => {
    setCpfCnpj(maskCpfCnpj(e.target.value));
  };

  const handleEmpresaSave = async () => {
    setEmpresaLoading(true);
    setEmpresaSuccess('');
    setEmpresaError('');
    try {
      await api.put('/tenant/configuracoes', {
        nomeResponsavel: nomeResponsavel.trim(),
        cpfCnpj: cpfCnpj.replace(/\D/g, ''),
      });
      setEmpresaSuccess('Configurações salvas com sucesso!');
      toast.success('Dados da empresa atualizados!');
      refreshTenant?.();
      setTimeout(() => setEmpresaSuccess(''), 4000);
    } catch (err) {
      const d = err.response?.data?.detail;
      setEmpresaError(typeof d === 'string' ? d : 'Erro ao salvar configurações.');
      toast.error('Erro ao atualizar configurações.');
    } finally {
      setEmpresaLoading(false);
    }
  };

  // Carrega categorias
  const loadCategorias = useCallback(() => {
    setCatLoading(true);
    api.get('/categorias')
      .then(r => setCategorias(r.data))
      .catch(() => {})
      .finally(() => setCatLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'categorias') loadCategorias();
  }, [tab, loadCategorias]);

  const handleAddCategoria = async (data) => {
    await api.post('/categorias', data);
    setShowAddForm(false);
    loadCategorias();
    toast.success('Categoria criada!');
  };

  const handleEditCategoria = async (data) => {
    await api.put(`/categorias/${editingCat.id}`, data);
    setEditingCat(null);
    loadCategorias();
    toast.success('Categoria atualizada!');
  };

  const confirmDeleteCategoria = async () => {
    if (!deletingCat) return;
    try {
      await api.delete(`/categorias/${deletingCat.id}`);
      setDeletingCat(null);
      loadCategorias();
      toast.success('Categoria removida!');
    } catch {
      toast.error('Erro ao remover categoria.');
    }
  };

  // Troca de Senha (Segurança)
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!senhaAtual || !senhaNova) {
      toast.error('Preencha a senha atual e a nova senha.');
      return;
    }
    if (senhaNova.length < 8) {
      toast.error('A nova senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (senhaNova !== senhaConfirma) {
      toast.error('A confirmação da senha não confere.');
      return;
    }
    setSenhaLoading(true);
    try {
      await api.put('/auth/me/senha', { senhaAtual, senhaNova });
      toast.success('Senha atualizada com sucesso!');
      setSenhaAtual('');
      setSenhaNova('');
      setSenhaConfirma('');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erro ao alterar senha.');
    } finally {
      setSenhaLoading(false);
    }
  };

  // Carrega Sessões (Dispositivos Conectados)
  const loadSessoes = useCallback(async () => {
    setSessoesLoading(true);
    try {
      const res = await api.get('/usuarios/me/sessoes');
      setSessoes(res.data || []);
    } catch {
      toast.error('Erro ao carregar dispositivos conectados.');
    } finally {
      setSessoesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'dispositivos') {
      loadSessoes();
    }
  }, [tab, loadSessoes]);

  const handleRevokeSession = async (sessaoId) => {
    try {
      await api.delete(`/usuarios/me/sessoes/${sessaoId}`);
      toast.success('Dispositivo desconectado!');
      loadSessoes();
    } catch {
      toast.error('Erro ao desconectar sessão.');
    }
  };

  // ── Tab Dashboard (cards personalizáveis) ──
  const dashCards = resolveDashboardCards(user);

  const saveDashCards = async (next) => {
    const previous = user;
    refreshUser({ ...user, preferencias: { ...(user?.preferencias || {}), dashboardCards: next } });
    try {
      const res = await api.put('/auth/me/preferencias', { dashboardCards: next });
      refreshUser(res.data);
    } catch {
      refreshUser(previous);
      toast.error('Não foi possível salvar. Tente novamente.');
    }
  };

  const toggleDashCard = (id, value) => saveDashCards({ ...dashCards, [id]: value });
  const applyPreset = (key) => {
    saveDashCards(PRESETS[key].build());
    toast.success(`${PRESETS[key].label} aplicado ao dashboard.`);
  };

  const settingsGroups = [
    {
      title: 'Conta',
      items: [
        { id: 'empresa',      label: 'Dados da Empresa',        icon: Building2 },
        { id: 'categorias',   label: 'Categorias',              icon: Tag },
        { id: 'seguranca',    label: 'Segurança',               icon: Shield },
        { id: 'dispositivos', label: 'Dispositivos Conectados', icon: Monitor },
      ],
    },
    {
      title: 'Sistema',
      items: [
        { id: 'preferencias', label: 'Preferências',  icon: Sliders },
        { id: 'notificacoes', label: 'Notificações',  icon: Bell },
        { id: 'dashboard',    label: 'Dashboard',     icon: LayoutGrid },
      ],
    },
  ];

  const filteredCats = filterTipo
    ? categorias.filter(c => c.tipo === filterTipo)
    : categorias;

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto space-y-6 page-enter pb-16">
      {/* Header */}
      <div>
        <h1
          className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2.5"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          Configurações do Sistema
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Gerencie dados da empresa, categorias, preferências de uso, segurança e sessões ativas
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
      <SettingsNav
        groups={settingsGroups}
        active={tab}
        onChange={setTab}
        accentColor={accent}
      />
      <div className="flex-1 min-w-0 space-y-6">

      {/* ─── 1. ABA DADOS DA EMPRESA ─── */}
      {tab === 'empresa' && (
        <div className="fin-card p-5 sm:p-6 space-y-5 rounded-2xl border border-slate-200/80 dark:border-slate-800">
          <div>
            <h2 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">
              Informações da Empresa / Responsável
            </h2>
            <p className="text-xs text-slate-400">
              Esses dados são usados em relatórios e cabeçalhos gerenciais do sistema.
            </p>
          </div>

          {empresaError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600 flex items-center gap-2">
              <AlertCircle size={15} />
              <span>{empresaError}</span>
            </div>
          )}
          {empresaSuccess && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 flex items-center gap-2">
              <CheckCircle2 size={15} />
              <span>{empresaSuccess}</span>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="fin-label">Nome da Empresa ou Responsável *</label>
              <input
                className="fin-input"
                value={nomeResponsavel}
                onChange={e => setNomeResponsavel(e.target.value)}
                placeholder="Ex: João Silva ou Gastify Consultoria Ltda"
              />
            </div>
            <div>
              <label className="fin-label">CPF ou CNPJ</label>
              <input
                className="fin-input"
                value={cpfCnpj}
                onChange={handleCpfCnpjChange}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength={18}
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Formatação automática aplicada conforme você digita.
              </p>
            </div>
          </div>

          <div className="flex justify-end pt-3 border-t border-slate-100 dark:border-slate-800">
            <button
              onClick={handleEmpresaSave}
              disabled={empresaLoading}
              className="nexus-btn-primary px-5 py-2.5 rounded-xl text-xs font-bold inline-flex items-center gap-2"
            >
              {empresaLoading ? <RefreshCw size={14} className="animate-spin" /> : <Check size={15} />}
              <span>{empresaLoading ? 'Salvando...' : 'Salvar Alterações'}</span>
            </button>
          </div>
        </div>
      )}

      {/* ─── 2. ABA CATEGORIAS ─── */}
      {tab === 'categorias' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {[{ value: '', label: 'Todas' }, ...TIPOS.map(t => ({ value: t, label: TIPO_LABEL[t] }))].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFilterTipo(opt.value)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors ${
                    filterTipo === opt.value
                      ? 'text-white border-transparent'
                      : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300'
                  }`}
                  style={filterTipo === opt.value ? { background: accent, borderColor: accent } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setShowAddForm(true); setEditingCat(null); }}
              className="nexus-btn-primary px-4 py-2 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 flex-shrink-0"
            >
              <Plus size={15} /> Nova Categoria
            </button>
          </div>

          {/* Form inline */}
          {showAddForm && (
            <div className="fin-card p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Cadastrar Categoria
              </h3>
              <form
                onSubmit={e => {
                  e.preventDefault();
                  const nome = e.target.nome.value.trim();
                  const tipo = e.target.tipo.value;
                  const desc = e.target.descricao.value.trim();
                  if (!nome) return;
                  handleAddCategoria({ nome, tipo, descricao: desc });
                }}
                className="space-y-3"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="fin-label">Nome da Categoria *</label>
                    <input name="nome" className="fin-input" placeholder="Ex: Software, Aluguel, Vendas..." required />
                  </div>
                  <div>
                    <label className="fin-label">Tipo *</label>
                    <select name="tipo" className="fin-input">
                      {TIPOS.map(t => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="fin-label">Descrição (Opcional)</label>
                  <input name="descricao" className="fin-input" placeholder="Detalhes opcionais..." />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="nexus-btn-primary px-4 py-2 rounded-xl text-xs font-bold"
                  >
                    Salvar
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Lista de Categorias */}
          {catLoading ? (
            <div className="text-center py-10 text-slate-400 text-xs">Carregando categorias...</div>
          ) : filteredCats.length === 0 ? (
            <div className="text-center py-10 text-slate-400 text-xs">
              {filterTipo ? `Nenhuma categoria do tipo ${TIPO_LABEL[filterTipo]}.` : 'Nenhuma categoria cadastrada.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCats.map(cat => (
                <div
                  key={cat.id}
                  className="fin-card flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-slate-200/70 dark:border-slate-800"
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md ${TIPO_COLOR[cat.tipo]}`}>
                      {TIPO_LABEL[cat.tipo]}
                    </span>
                    <span className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {cat.nome}
                    </span>
                    {cat.descricao && (
                      <span className="text-[11px] text-slate-400 hidden sm:inline truncate max-w-[200px]">
                        • {cat.descricao}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setDeletingCat(cat)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
                      title="Excluir categoria"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ─── 3. ABA PREFERÊNCIAS ─── */}
      {tab === 'preferencias' && (
        <div className="fin-card p-5 sm:p-6 space-y-6 rounded-2xl border border-slate-200/80 dark:border-slate-800">
          <div>
            <h2 className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">
              Preferências Visuais e Regionais
            </h2>
            <p className="text-xs text-slate-400">
              Personalize a aparência da interface e consulte os padrões de exibição monetária.
            </p>
          </div>

          <div className="space-y-5 divide-y divide-slate-100 dark:divide-slate-800">
            {/* Tema */}
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-slate-800 dark:text-slate-100 block">
                  Tema da Interface
                </span>
                <span className="text-[11px] text-slate-400 block mt-0.5">
                  Alternar entre modo Claro (suave) e Escuro (alto contraste)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleTheme}
                  className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 shadow-sm flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                >
                  {theme === 'dark' ? (
                    <>
                      <Sun size={15} className="text-amber-400" />
                      <span>Modo Claro</span>
                    </>
                  ) : (
                    <>
                      <Moon size={15} className="text-indigo-400" />
                      <span>Modo Escuro</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Moeda Padrão */}
            <div className="pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-slate-800 dark:text-slate-100 block">
                  Formato de Moeda
                </span>
                <span className="text-[11px] text-slate-400 block mt-0.5">
                  Moeda e formatação de valores em toda a aplicação
                </span>
              </div>
              <div>
                <span className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold border border-slate-200 dark:border-slate-700 flex items-center gap-1.5">
                  <span>Real Brasileiro (BRL • R$)</span>
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.2 bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded">Fixo</span>
                </span>
              </div>
            </div>

            {/* Idioma */}
            <div className="pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="text-xs font-bold text-slate-800 dark:text-slate-100 block">
                  Idioma da Plataforma
                </span>
                <span className="text-[11px] text-slate-400 block mt-0.5">
                  Localização de datas, filtros e expressões do sistema
                </span>
              </div>
              <div>
                <span className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs font-bold border border-slate-200 dark:border-slate-700">
                  Português (Brasil)
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── 4. ABA NOTIFICAÇÕES ─── */}
      {tab === 'notificacoes' && (
        <div className="fin-card p-6 sm:p-10 rounded-2xl border border-slate-200/80 dark:border-slate-800 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center mx-auto shadow-sm">
            <Bell size={28} />
          </div>
          <div className="max-w-md mx-auto space-y-1.5">
            <div className="flex items-center justify-center gap-2">
              <h2 className="text-base font-bold text-slate-800 dark:text-white">
                Notificações & Alertas
              </h2>
              <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                Em Breve
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              O módulo de alertas inteligentes está sendo preparado. Em breve você poderá receber notificações push e resumos periódicos sobre contas a pagar, vencimentos de faturas e metas atingidas.
            </p>
          </div>
        </div>
      )}

      {/* ─── 5. ABA DASHBOARD ─── */}
      {tab === 'dashboard' && (
        <div className="space-y-5" data-testid="config-dashboard-tab">
          <div className="fin-card p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-4">
            <div>
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">Personalize seu Dashboard</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Escolha o que aparece na tela inicial. As mudanças são salvas e aplicadas na hora.
                O Gastify atende tanto pessoa física quanto empresas: use um perfil pronto como ponto de partida e depois ajuste card a card.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => applyPreset(key)}
                  data-testid={`dash-preset-${key}`}
                  className="text-left p-3.5 rounded-xl border border-slate-200/80 dark:border-slate-700 hover:border-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/30 transition-colors cursor-pointer"
                >
                  <span className="block text-xs font-bold text-slate-800 dark:text-slate-100">{p.label}</span>
                  <span className="block text-[11px] text-slate-400 mt-0.5">{p.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {DASHBOARD_GROUPS.map(group => (
            <div key={group.id} className="fin-card p-5 sm:p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 space-y-4">
              <div className="pb-3 border-b border-slate-100 dark:border-slate-800">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{group.titulo}</h3>
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                    group.id === 'empresarial'
                      ? 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'
                  }`}>
                    {group.publico}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{group.desc}</p>
              </div>

              <div className="space-y-2.5">
                {DASHBOARD_CARDS.filter(c => c.group === group.id).map(card => (
                  <div
                    key={card.id}
                    className="p-3.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40 flex items-center justify-between gap-4"
                  >
                    <div className="min-w-0">
                      <span className="text-xs font-bold text-slate-800 dark:text-slate-200 block">{card.label}</span>
                      <span className="text-[11px] text-slate-400 block mt-0.5">{card.desc}</span>
                    </div>
                    <Toggle
                      checked={!!dashCards[card.id]}
                      onChange={(v) => toggleDashCard(card.id, v)}
                      label={`Mostrar ${card.label} no dashboard`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── 6. ABA SEGURANÇA ─── */}
      {tab === 'seguranca' && (
        <div className="space-y-6">
          {/* Trocar Senha */}
          <div className="fin-card p-5 sm:p-6 space-y-4 rounded-2xl border border-slate-200/80 dark:border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <Lock size={16} />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800 dark:text-white">
                  Alterar Senha de Acesso
                </h3>
                <p className="text-xs text-slate-400">
                  Atualize sua senha para manter sua conta sempre protegida
                </p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-3.5 max-w-md pt-2">
              <div>
                <label className="fin-label">Senha Atual *</label>
                <input
                  type="password"
                  value={senhaAtual}
                  onChange={e => setSenhaAtual(e.target.value)}
                  className="fin-input"
                  placeholder="Digite sua senha atual"
                  required
                />
              </div>
              <div>
                <label className="fin-label">Nova Senha (mínimo 8 caracteres) *</label>
                <input
                  type="password"
                  value={senhaNova}
                  onChange={e => setSenhaNova(e.target.value)}
                  className="fin-input"
                  placeholder="Nova senha segura"
                  minLength={8}
                  required
                />
              </div>
              <div>
                <label className="fin-label">Confirmar Nova Senha *</label>
                <input
                  type="password"
                  value={senhaConfirma}
                  onChange={e => setSenhaConfirma(e.target.value)}
                  className="fin-input"
                  placeholder="Repita a nova senha"
                  required
                />
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={senhaLoading}
                  className="nexus-btn-primary px-5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5"
                >
                  {senhaLoading && <RefreshCw size={14} className="animate-spin" />}
                  <span>Salvar Nova Senha</span>
                </button>
              </div>
            </form>
          </div>

          {/* Troca de E-mail com Confirmação */}
          <div className="fin-card p-5 sm:p-6 space-y-3 rounded-2xl border border-slate-200/80 dark:border-slate-800">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 flex items-center justify-center">
                <Mail size={16} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-bold text-slate-800 dark:text-white">
                    Alteração de E-mail
                  </h3>
                  <span className="px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider bg-slate-200 dark:bg-slate-800 text-slate-500">
                    Em Breve
                  </span>
                </div>
                <p className="text-xs text-slate-400">
                  Por medidas de segurança bancária, a alteração de e-mail dependerá de confirmação por link transacional. O serviço de envio de e-mails será ativado na próxima etapa.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── 7. ABA DISPOSITIVOS CONECTADOS ─── */}
      {tab === 'dispositivos' && (
        <div className="fin-card p-5 sm:p-6 space-y-5 rounded-2xl border border-slate-200/80 dark:border-slate-800">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
            <div>
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Dispositivos Conectados
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Sessões ativas na sua conta. Você pode encerrar o acesso de qualquer dispositivo remotamente.
              </p>
            </div>
            <button
              onClick={loadSessoes}
              className="px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 inline-flex items-center gap-1.5 self-start sm:self-auto cursor-pointer"
            >
              <RefreshCw size={13} className={sessoesLoading ? 'animate-spin' : ''} />
              <span>Atualizar</span>
            </button>
          </div>

          {sessoesLoading && sessoes.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <RefreshCw size={14} className="animate-spin" />
              <span>Carregando dispositivos...</span>
            </div>
          ) : sessoes.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">
              Nenhuma sessão ativa encontrada.
            </div>
          ) : (
            <div className="space-y-3">
              {sessoes.map(sessao => {
                const isMobile = /mobile|android|iphone|ipad/i.test(sessao.userAgent || '');
                return (
                  <div
                    key={sessao.id}
                    className="p-4 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/70 dark:border-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 flex-shrink-0">
                        {isMobile ? <Smartphone size={18} /> : <Laptop size={18} />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-800 dark:text-slate-100">
                            {isMobile ? 'Dispositivo Móvel' : 'Computador / Desktop'}
                          </span>
                          {sessao.atual && (
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400">
                              Esta Sessão
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5 space-x-2">
                          <span>IP: {sessao.ip || '127.0.0.1'}</span>
                          <span>•</span>
                          <span>Último uso: {sessao.ultimoUso ? new Date(sessao.ultimoUso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'Recente'}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      {!sessao.atual ? (
                        <button
                          type="button"
                          onClick={() => handleRevokeSession(sessao.id)}
                          className="px-3 py-1.5 rounded-xl text-xs font-semibold text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <LogOut size={13} />
                          <span>Desconectar</span>
                        </button>
                      ) : (
                        <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                          Conectado
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal confirmação exclusão categoria */}
      {deletingCat && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={() => setDeletingCat(null)}
        >
          <div className="nexus-modal p-6 w-full max-w-sm rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 mb-2">
              Remover categoria
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Tem certeza que deseja remover <strong>"{deletingCat.nome}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDeletingCat(null)} className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-500">Cancelar</button>
              <button
                onClick={confirmDeleteCategoria}
                className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-rose-600 hover:bg-rose-700"
              >
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
}
